// src/context/NotificationContext.jsx
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import socket from '../services/socket';
import { AuthContext } from './AuthContext';
import { nombreVisible } from '../utils/nombreCorto';
import { toast } from 'react-toastify';
import { notify } from '../utils/notify';
import { playAuthRequestSound } from '../utils/sound';

export const NotificationContext = createContext(null);

const SESSION_KEY = 'notifications_v1';

function loadFromSession() {
    try {
        const raw = sessionStorage.getItem(SESSION_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

function saveToSession(notifs) {
    try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(notifs.slice(0, 50)));
    } catch {}
}

let nextId = Date.now();

export function NotificationProvider({ children }) {
    const { user } = useContext(AuthContext);
    const [notifications, setNotifications] = useState(loadFromSession);
    const notifRef  = useRef(notifications);
    // Ref para que los handlers de socket siempre vean los departamentos actuales
    const myDeptsRef = useRef([]);

    useEffect(() => {
        notifRef.current = notifications;
        saveToSession(notifications);
    }, [notifications]);

    useEffect(() => {
        myDeptsRef.current = (user?.departments || []).map(d => String(d._id || d));
    }, [user]);

    const addNotif = (partial) => {
        const notif = {
            id:        String(++nextId),
            read:      false,
            timestamp: new Date().toISOString(),
            ...partial,
        };
        setNotifications(prev => [notif, ...prev].slice(0, 50));
    };

    useEffect(() => {
        if (!user) return;
        const uid     = String(user.id);
        const role    = user.role;

        // Helpers — usan ref para siempre ver el valor actualizado
        const inMyDept = (deptField) =>
            myDeptsRef.current.includes(String(deptField?._id || deptField || ''));

        const isAssignedToMe = (t) =>
            String(t?.assignedTo?._id  || t?.assignedTo  || '') === uid ||
            String(t?.assignedTo2?._id || t?.assignedTo2 || '') === uid;

        const isMineCreated = (t) =>
            String(t?.createdBy?._id || t?.createdBy || '') === uid;

        // Helper: ¿soy autorizador asignado a este ticket?
        const isMyAuthorization = (t) =>
            String(t?.authorizer?._id  || t?.authorizer  || '') === uid ||
            String(t?.authorizer2?._id || t?.authorizer2 || '') === uid ||
            String(t?.authorizer3?._id || t?.authorizer3 || '') === uid;

        // ── ticketCreated ─────────────────────────────────────────────────────
        // admin: todos | leader/agent: solo su departamento | user/authorizer: ninguno
        const onTicketCreated = (ticket) => {
            if (role === 'admin') {
                // recibe todos
            } else if (role === 'leader' || role === 'agent') {
                if (!inMyDept(ticket.department)) return;
            } else {
                return; // usuarios y autorizadores no reciben alerta de creación
            }
            addNotif({
                type:     'created',
                ticketId: ticket._id,
                folio:    ticket.folio,
                message:  `Nuevo ticket #${ticket.folio || '?'}: ${ticket.title}`,
            });
        };

        // ── ticketBumped (comentario público) ─────────────────────────────────
        // admin: todos | leader: su depto | agent: tickets asignados a él
        // user: tickets que creó
        const onTicketBumped = ({ ticketId, ticket, by }) => {
            if (role === 'admin') {
                // recibe todos
            } else if (role === 'leader') {
                if (!inMyDept(ticket?.department)) return;
            } else if (role === 'agent') {
                if (!isAssignedToMe(ticket)) return;
            } else if (role === 'authorizer') {
                // Solo tickets donde soy autorizador asignado o creador
                if (!isMyAuthorization(ticket) && !isMineCreated(ticket)) return;
            } else {
                // usuario final — solo sus tickets
                if (!isMineCreated(ticket)) return;
            }
            const folio = ticket?.folio;
            addNotif({
                type:     'bumped',
                ticketId: ticketId,
                folio,
                message:  `Nuevo comentario en #${folio || '?'} por ${nombreVisible(by, role) || 'alguien'}`,
            });
        };

        // ── ticketOverdue (cron, sin populate) ───────────────────────────────
        // Los campos son ObjectId raw. admin: todos | leader/agent: su depto | user: los suyos
        const onTicketOverdue = (ticket) => {
            if (role === 'admin') {
                // recibe todos
            } else if (role === 'leader' || role === 'agent') {
                if (!myDeptsRef.current.includes(String(ticket.department || ''))) return;
            } else if (role === 'authorizer') {
                // Solo si soy autorizador asignado o creador
                if (!isMyAuthorization(ticket) && String(ticket.createdBy || '') !== uid) return;
            } else {
                // usuario final
                if (String(ticket.createdBy || '') !== uid) return;
            }
            addNotif({
                type:     'overdue',
                ticketId: ticket._id,
                folio:    ticket.folio,
                message:  `Ticket #${ticket.folio || '?'} ha vencido`,
            });
        };

        // ── newAuthorizationRequest ───────────────────────────────────────────
        // Solo al autorizador 1, 2 o 3 asignado
        const onNewAuthRequest = (ticket) => {
            if (!isMyAuthorization(ticket)) return;
            addNotif({
                type:     'authorization',
                ticketId: ticket._id,
                folio:    ticket.folio,
                message:  `Solicitud de autorización: #${ticket.folio || '?'} — ${ticket.title}`,
            });
        };

        // ── ticketClosed ──────────────────────────────────────────────────────
        // Solo al creador del ticket; invitarlo a calificar
        const onTicketClosed = (ticket) => {
            if (String(ticket.createdBy) !== uid) return;
            addNotif({
                type:     'closed',
                ticketId: ticket._id,
                folio:    ticket.folio,
                message:  `Tu ticket #${ticket.folio || '?'} fue cerrado. ¡Califícanos!`,
            });
        };

        // ── autoAssignParticipantInactive ─────────────────────────────────────
        // Se dispara al desactivar un agente que estaba en la rotación de auto-asignación.
        // Notificamos a admin (todos los deptos) y a leader del depto afectado.
        const onAutoAssignInactive = ({ userName, deptId, deptName }) => {
            if (role === 'admin') {
                // recibe todos
            } else if (role === 'leader') {
                if (!myDeptsRef.current.includes(String(deptId))) return;
            } else {
                return;
            }
            addNotif({
                type:    'autoAssignInactive',
                deptId,
                message: `${userName} salió de la rotación de auto-asignación de ${deptName} (fue desactivado).`,
            });
        };

        // ── cancelacionReminder (cron cada 4 h de turno por ticket pendiente) ──
        // Solo staff: admin todos | leader/agent su depto. Lleva sonido y toast
        // porque la idea es que no se olvide.
        const onCancelacionReminder = (t) => {
            if (role === 'admin') {
                // recibe todos
            } else if (role === 'leader' || role === 'agent') {
                if (!myDeptsRef.current.includes(String(t.department || ''))) return;
            } else {
                return;
            }
            const horas = Number(t.horasAbierto) || 0;
            const msg = `Cancelación pendiente: #${t.folio || '?'} · ${t.title || ''}` +
                        (horas ? ` (abierto hace ${horas} h)` : '');
            addNotif({ type: 'cancelacion', ticketId: t._id, folio: t.folio, message: msg });
            playAuthRequestSound();
            toast.warn(msg, { autoClose: 12000 });
            notify('Cancelación pendiente', msg);
        };

        // ── cancelacionShiftAlert (20 min antes de la salida del agente) ──────
        const onCancelacionShiftAlert = (a) => {
            const n = Number(a?.count) || 0;
            if (!n) return;
            const msg = `Sales a las ${a.horaSalida || ''} y tienes ${n} ticket${n === 1 ? '' : 's'} de cancelación pendiente${n === 1 ? '' : 's'}: ` +
                        (a.tickets || []).map(t => `#${t.folio}`).join(', ');
            addNotif({ type: 'cancelacion', ticketId: a.tickets?.[0]?._id, folio: a.tickets?.[0]?.folio, message: msg });
            playAuthRequestSound();
            toast.error(msg, { autoClose: false, closeOnClick: false });
            notify('Cancelaciones pendientes antes de salir', msg);
        };

        // ── Guardias de sábado (van directo al agente por su room user:<id>) ──
        const fechaGuardia = (iso) => {
            try { return new Date(iso + 'T12:00:00').toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' }); }
            catch { return iso; }
        };
        const turno = (g) => g.slot
            ? `${g.slot.label || (g.slot.mode === 'home' ? 'Home office' : 'Oficina')} ${g.slot.start}–${g.slot.end}`
            : `${g.start || ''}–${g.end || ''}`;
        const onGuardAssigned = (g) => {
            const msg = `${g.reason === 'changed' ? 'Cambio de guardia' : 'Tienes guardia'}: ${fechaGuardia(g.date)} · ` +
                        `${turno(g)} · ${g.department?.name || 'tu departamento'}`;
            addNotif({ type: 'guard', message: msg, link: '/guardias' });
            toast.info(msg, { autoClose: 12000 });
            notify('Guardia de sábado', msg);
        };
        const onGuardReminder = (g) => {
            const msg = `Recordatorio: mañana ${fechaGuardia(g.date)} tienes guardia · ${turno(g)} · ${g.department?.name || 'tu departamento'}`;
            addNotif({ type: 'guard', message: msg, link: '/guardias' });
            toast.warn(msg, { autoClose: false });
            notify('Guardia mañana', msg);
        };
        const onGuardRemoved = (g) => {
            const msg = `Ya no tienes guardia el ${fechaGuardia(g.date)} (${turno(g)}) en ${g.department?.name || 'tu departamento'}`;
            addNotif({ type: 'guard', message: msg, link: '/guardias' });
            toast.info(msg, { autoClose: 12000 });
        };

        socket.on('guardAssigned',           onGuardAssigned);
        socket.on('guardReminder',           onGuardReminder);
        socket.on('guardRemoved',            onGuardRemoved);
        socket.on('ticketCreated',           onTicketCreated);
        socket.on('cancelacionReminder',     onCancelacionReminder);
        socket.on('cancelacionShiftAlert',   onCancelacionShiftAlert);
        socket.on('ticketBumped',            onTicketBumped);
        socket.on('ticketOverdue',           onTicketOverdue);
        socket.on('newAuthorizationRequest', onNewAuthRequest);
        socket.on('ticketClosed',            onTicketClosed);
        socket.on('autoAssignParticipantInactive', onAutoAssignInactive);

        return () => {
            socket.off('guardAssigned',           onGuardAssigned);
            socket.off('guardReminder',           onGuardReminder);
            socket.off('guardRemoved',            onGuardRemoved);
            socket.off('ticketCreated',           onTicketCreated);
            socket.off('cancelacionReminder',     onCancelacionReminder);
            socket.off('cancelacionShiftAlert',   onCancelacionShiftAlert);
            socket.off('ticketBumped',            onTicketBumped);
            socket.off('ticketOverdue',           onTicketOverdue);
            socket.off('newAuthorizationRequest', onNewAuthRequest);
            socket.off('ticketClosed',            onTicketClosed);
            socket.off('autoAssignParticipantInactive', onAutoAssignInactive);
        };
    }, [user]);

    const markAllRead = () => setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    const clearAll    = () => setNotifications([]);
    const markRead    = (id) => setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));

    const unreadCount = notifications.filter(n => !n.read).length;

    return (
        <NotificationContext.Provider value={{ notifications, unreadCount, markAllRead, clearAll, markRead }}>
            {children}
        </NotificationContext.Provider>
    );
}
